import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { HeadingContext } from '../Heading';
import { useLabelProps } from '../util/labeling';

export type PanelKind = 'danger';
export type PanelVariant = 'opened' | 'enclosed';

export type PanelProps = React.ComponentPropsWithoutRef<'section'> & {
    /**
     * Kind of the panel.
     * - `danger` - Panel with a danger color. Used to highlight dangerous actions.
     */
    kind?: PanelKind | undefined;

    /**
     * Variant of the panel.
     * - `opened` - Title outside of the panel.
     * - `enclosed` - Title enclosed in the panel.
     * Useful to play panels in an already opened layout like a side-panel.
     * @default 'opened'
     * @deprecated The enclosed version is temporary and not yet validated by design.
     */
    variant?: PanelVariant | undefined;
};

export const PanelContext = React.createContext<ContextValue<PanelProps, HTMLElement>>({});

/**
 * Panel view with a title.
 * <Panel>
 *  <PanelHeader>
 *   <Heading>Title</Heading>
 *   <Description>Description</Description>
 *  </PanelHeader>
 *  <PanelBody>
 *   <Content />
 *  </PanelBody>
 * </Panel>
 */
export const Panel = React.forwardRef(function Panel(
    props: PanelProps,
    ref: React.ForwardedRef<HTMLElement>
) {
    [props, ref] = useContextProps(props, ref, PanelContext);
    const { kind, variant = 'opened', ...rest } = props;
    const headingId = useSlotId();
    const labelProps = useLabelProps({ ...props, headingId, descriptionId: null });
    return (
        <HeadingContext.Provider
            value={{
                id: headingId,
                level: 2,
                className:
                    variant === 'enclosed' ? 'text-ui-heading-small' : 'text-ui-heading-medium',
            }}
        >
            <section
                ref={ref}
                {...rest}
                data-kind={kind}
                data-variant={variant}
                className={clsx(
                    'group/panel',
                    {
                        opened: 'flex w-full flex-col gap-3',
                        enclosed: clsx(
                            'flex w-full flex-col',
                            'rounded-lg border bg-base',
                            'data-[kind=danger]:border-danger',
                            String.raw`[&>*:first-child:not(.group\/panel-body)]:border-b [&>*:first-child:not(.group\/panel-body)]:p-4`
                        ),
                    }[variant],
                    rest.className
                )}
                {...labelProps}
            />
        </HeadingContext.Provider>
    );
});

export type PanelHeaderProps = React.ComponentPropsWithoutRef<'div'>;

/**
 * Header of a panel.
 * <PanelHeader>
 *  <Heading>Title</Heading>
 *  <Description>Description</Description>
 * </PanelHeader>
 */
export const PanelHeader = React.forwardRef(function PanelHeader(
    props: PanelHeaderProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    return <div ref={ref} {...props} className={clsx('flex flex-col gap-0.5', props.className)} />;
});

export type PanelBodyProps = React.ComponentPropsWithoutRef<'div'>;

/**
 * Body of a panel.
 */
export const PanelBody = React.forwardRef(function PanelBody(
    props: PanelBodyProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    return (
        <HeadingContext.Provider value={{ level: 3, className: 'text-ui-heading-small' }}>
            <div
                ref={ref}
                {...props}
                className={clsx(
                    'group/panel-body',
                    'flex flex-col gap-6 p-4',
                    'group-data-[variant=opened]/panel:rounded-lg group-data-[variant=opened]/panel:border group-data-[variant=opened]/panel:bg-base',
                    'group-data-[variant=opened]/panel:group-data-[kind=danger]/panel:border-danger',
                    props.className
                )}
            />
        </HeadingContext.Provider>
    );
});

export type PanelSetProps = React.ComponentPropsWithoutRef<'div'> & {
    /**
     * Variant of the panel.
     * - `opened` - Title outside of the panel.
     * - `enclosed` - Title enclosed in the panel.
     * Useful to play panels in an already opened layout like a side-panel.
     * @default 'opened'
     * @deprecated The enclosed version is temporary and not yet validated by design.
     */
    variant?: PanelVariant | undefined;
};

export const PanelSetContext = React.createContext<ContextValue<PanelSetProps, HTMLDivElement>>({});

/**
 * A group of several panels.
 */
export const PanelSet = React.forwardRef(function PanelSet(
    props: PanelSetProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, PanelSetContext);
    const { variant = 'opened', ...rest } = props;
    return (
        <PanelContext.Provider value={{ variant }}>
            <div
                ref={ref}
                data-variant={props.variant}
                {...rest}
                className={clsx(
                    'group/panel-set',
                    'flex flex-col',
                    { opened: 'gap-8', enclosed: 'gap-6' }[variant],
                    props.className
                )}
            />
        </PanelContext.Provider>
    );
});
